/**
 * `ColumnDef` lists for the contract tables — customer (sell-side) and partner
 * (buy-side). Columns mirror the legacy `getContractColumns` /
 * `createPartnerContractColumns` core subset 1-1, built on the shared contract
 * cell renderers + `src/lib/format` formatters.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import type { ContractRow, PartnerContractRow } from '@/api/contracts';
import {
  contractCustomerCell,
  contractNumberCell,
  contractStatusCell,
  dateCell,
  directionCell,
  moneyCell,
  roleCell,
  serviceCell,
  textCell,
} from './contract-cell-renderers';

export type CustomerContractCol = ColumnDef<ContractRow, unknown>;
export type PartnerContractCol = ColumnDef<PartnerContractRow, unknown>;

const rightHeader = (label: string) => () => (
  <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>
);

/* ── Customer (sell-side) contract columns ──────────────────────────────── */

export const customerContractColumns: CustomerContractCol[] = [
  {
    id: 'contract_number_id',
    header: 'Contract',
    cell: ({ row }) => contractNumberCell(row.original),
  },
  {
    id: 'customer',
    header: 'Customer',
    cell: ({ row }) => contractCustomerCell(row.original),
  },
  {
    id: 'service',
    header: 'Service',
    cell: ({ row }) => serviceCell(row.original),
  },
  {
    id: 'contract_status',
    header: 'Status',
    cell: ({ row }) => contractStatusCell(row.original.contract_status),
  },
  {
    id: 'contract_role',
    header: 'Role',
    cell: ({ row }) => roleCell(row.original.contract_role),
  },
  {
    id: 'contract_direction',
    header: 'Direction',
    cell: ({ row }) => directionCell(row.original.contract_direction),
  },
  {
    id: 'contract_value',
    header: rightHeader('Value'),
    cell: ({ row }) => moneyCell(row.original.contract_value),
  },
  {
    id: 'contract_effective_date',
    header: rightHeader('Effective'),
    cell: ({ row }) => dateCell(row.original.contract_effective_date),
  },
  {
    id: 'contract_expiry_date',
    header: rightHeader('Expiry'),
    cell: ({ row }) => dateCell(row.original.contract_expiry_date),
  },
  {
    id: 'created_at',
    header: rightHeader('Created'),
    cell: ({ row }) => dateCell(row.original.created_at),
  },
];

/* ── Partner (buy-side) contract columns ────────────────────────────────── */

export const partnerContractColumns: PartnerContractCol[] = [
  {
    id: 'contract_number_id',
    header: 'Contract',
    cell: ({ row }) => textCell(row.original.contract_number_id || `#${row.original.contract_id}`),
  },
  {
    id: 'partner_name',
    header: 'Supplier',
    cell: ({ row }) => textCell(row.original.partner_name),
  },
  {
    id: 'partner_code',
    header: 'Code',
    cell: ({ row }) => textCell(row.original.partner_code),
  },
  {
    id: 'contract_status',
    header: 'Status',
    cell: ({ row }) => contractStatusCell(row.original.contract_status),
  },
  {
    id: 'contract_value',
    header: rightHeader('Value'),
    cell: ({ row }) => moneyCell(row.original.contract_value),
  },
  {
    id: 'contract_effective_date',
    header: rightHeader('Effective'),
    cell: ({ row }) => dateCell(row.original.contract_effective_date),
  },
  {
    id: 'contract_expiry_date',
    header: rightHeader('Expiry'),
    cell: ({ row }) => dateCell(row.original.contract_expiry_date),
  },
  {
    id: 'employee_name',
    header: 'Created by',
    cell: ({ row }) => textCell(row.original.employee_name),
  },
  {
    id: 'created_at',
    header: rightHeader('Created'),
    cell: ({ row }) => dateCell(row.original.created_at),
  },
];
